import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { themeAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'

interface Theme {
  id: number
  name: string
  is_system: boolean
  is_dark: boolean
  primary_color: string
  secondary_color: string
  accent_color: string
  background_color: string
  surface_color: string
  text_primary: string
  text_secondary: string
  border_color: string
  widget_background: string
  widget_border: string
  widget_shadow: string
  font_family: string
  font_size_base: string
  border_radius: string
  custom_css?: string
}

interface ThemeContextType {
  currentTheme: Theme | null
  themes: Theme[]
  loading: boolean
  setTheme: (themeId: number) => Promise<void>
  refreshThemes: () => Promise<void>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null)
  const [themes, setThemes] = useState<Theme[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTheme()
    loadAllThemes()
  }, [])

  useEffect(() => {
    if (currentTheme) {
      applyTheme(currentTheme)
    }
  }, [currentTheme])

  const loadTheme = async () => {
    try {
      // Only try to load user's theme if authenticated
      // For non-authenticated users, use default theme
      const token = localStorage.getItem('token') || useAuthStore?.getState?.()?.token
      if (token) {
        const { data } = await themeAPI.getCurrent()
        setCurrentTheme(data)
      } else {
        // Set default light theme for non-authenticated users
        setCurrentTheme({
          id: 1,
          name: 'Light',
          is_system: true,
          is_dark: false,
          primary_color: '#0183c6',
          secondary_color: '#10B981',
          accent_color: '#F59E0B',
          background_color: '#F9FAFB',
          surface_color: '#FFFFFF',
          text_primary: '#111827',
          text_secondary: '#6B7280',
          border_color: '#E5E7EB',
          widget_background: '#FFFFFF',
          widget_border: '#E5E7EB',
          widget_shadow: '0 1px 3px rgba(0,0,0,0.1)',
          font_family: 'Inter, system-ui, sans-serif',
          font_size_base: '14px',
          border_radius: '8px'
        })
      }
    } catch (error) {
      console.error('Failed to load theme:', error)
      // On error, still set a default theme
      setCurrentTheme({
        id: 1,
        name: 'Light',
        is_system: true,
        is_dark: false,
        primary_color: '#0183c6',
        secondary_color: '#10B981',
        accent_color: '#F59E0B',
        background_color: '#F9FAFB',
        surface_color: '#FFFFFF',
        text_primary: '#111827',
        text_secondary: '#6B7280',
        border_color: '#E5E7EB',
        widget_background: '#FFFFFF',
        widget_border: '#E5E7EB',
        widget_shadow: '0 1px 3px rgba(0,0,0,0.1)',
        font_family: 'Inter, system-ui, sans-serif',
        font_size_base: '14px',
        border_radius: '8px'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadAllThemes = async () => {
    try {
      // Only load themes list for authenticated users
      const token = localStorage.getItem('token') || useAuthStore?.getState?.()?.token
      if (token) {
        const { data } = await themeAPI.getAll()
        setThemes(data.themes || [])
      } else {
        // For non-authenticated users, just set empty themes list
        setThemes([])
      }
    } catch (error) {
      console.error('Failed to load themes:', error)
      setThemes([])
    }
  }

  const setTheme = async (themeId: number) => {
    try {
      await themeAPI.setUserTheme(themeId)
      const { data } = await themeAPI.getById(themeId)
      setCurrentTheme(data)
    } catch (error) {
      console.error('Failed to set theme:', error)
      throw error
    }
  }

  const refreshThemes = async () => {
    await loadAllThemes()
  }

  const applyTheme = (theme: Theme) => {
    const root = document.documentElement

    // Apply CSS variables
    root.style.setProperty('--color-primary', theme.primary_color)
    root.style.setProperty('--color-secondary', theme.secondary_color)
    root.style.setProperty('--color-accent', theme.accent_color)
    root.style.setProperty('--color-background', theme.background_color)
    root.style.setProperty('--color-surface', theme.surface_color)
    root.style.setProperty('--color-text-primary', theme.text_primary)
    root.style.setProperty('--color-text-secondary', theme.text_secondary)
    root.style.setProperty('--color-border', theme.border_color)
    root.style.setProperty('--color-widget-bg', theme.widget_background)
    root.style.setProperty('--color-widget-border', theme.widget_border)
    root.style.setProperty('--shadow-widget', theme.widget_shadow)
    root.style.setProperty('--font-family-base', theme.font_family)
    root.style.setProperty('--font-size-base', theme.font_size_base)
    root.style.setProperty('--border-radius', theme.border_radius)

    // Set dark mode class
    if (theme.is_dark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Apply custom CSS if exists
    const existingCustomStyle = document.getElementById('theme-custom-css')
    if (existingCustomStyle) {
      existingCustomStyle.remove()
    }
    
    if (theme.custom_css) {
      const style = document.createElement('style')
      style.id = 'theme-custom-css'
      style.textContent = theme.custom_css
      document.head.appendChild(style)
    }

    // Update body background
    document.body.style.backgroundColor = theme.background_color
    document.body.style.color = theme.text_primary
  }

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        themes,
        loading,
        setTheme,
        refreshThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}












